import { useEffect, useMemo, useState } from 'react'
import { WifiOff, Wifi } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { useCurrentShow } from '@/context/current-show'
import { useEntryIntakeManager, type PendingIntakeEntry } from '@/hooks/useEntryIntake'

function summarizePending(entries: PendingIntakeEntry[]): number {
  return entries.filter(entry => entry.status !== 'syncing').length
}

export function RunHud() {
  const { currentShow } = useCurrentShow()
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true
  )
  const stats = useEntryIntakeManager(currentShow?.id)

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [currentShow?.id])

  const content = useMemo(() => {
    const available = stats.availableCodes.length
    const pending = summarizePending(stats.pendingEntries)
    return { available, pending }
  }, [stats])

  if (!currentShow) return null

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 rounded-md border px-3 py-2 text-sm">
      <div className="flex items-center gap-1" title="Available entry codes">
        <Badge variant="secondary" className="text-xs uppercase">
          Codes
        </Badge>
        <span className="font-semibold">{content.available}</span>
      </div>
      <div className="flex items-center gap-1" title="Pending offline entries">
        <Badge
          variant={content.pending > 0 ? 'destructive' : 'outline'}
          className="text-xs uppercase"
        >
          Pending
        </Badge>
        <span className="font-semibold">{content.pending}</span>
      </div>
      <div className="flex items-center gap-2" title={isOnline ? 'Online' : 'Offline'}>
        {isOnline ? (
          <Wifi className="h-4 w-4 text-emerald-500" aria-hidden="true" />
        ) : (
          <WifiOff className="h-4 w-4 text-destructive" aria-hidden="true" />
        )}
        <span className="text-xs font-semibold text-muted-foreground">
          {isOnline ? 'Online' : 'Offline'}
        </span>
      </div>
    </div>
  )
}
